import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import type { ClientVacationPeriod } from '../../../../models/vacationClientView.types';
import { groupConflictNames, NO_CONFLICTS_LABEL, SHOW_DETAILS_LABEL, HIDE_DETAILS_LABEL, VIEW_REQUEST_LABEL } from '../../vacationClientPanel.definitions';
import {
    PERIOD_CARD_CONFLICT_BORDER_CLASS,
    PERIOD_CARD_SCHEDULED_BORDER_CLASS,
    PERIOD_CARD_DEFAULT_BORDER_CLASS,
} from '../../vacationClientPanel.definitions';
import { buildConflictPeople } from './clientPeriodCard.people';
import { buildConflictCalendar } from '../../vacationClientPanel.calendar';
import { ConflictBadges } from '../ConflictBadges';
import { PeriodHeader, ConflictPeoplePanel, CalendarPanel } from './components';

interface ClientPeriodCardProps {
    period: ClientVacationPeriod;
    clientName?: string;
    responsibleName: string;
    siblingPeriods: ClientVacationPeriod[];
    onOpenRequest?: (collaboratorId: string) => void;
    initiallyExpanded?: boolean;
}

export function ClientPeriodCard({
    period,
    clientName,
    responsibleName,
    siblingPeriods,
    onOpenRequest,
    initiallyExpanded = false,
}: ClientPeriodCardProps) {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

    const people = useMemo(() => buildConflictPeople(period, siblingPeriods), [period, siblingPeriods]);
    const calendarModel = useMemo(() => buildConflictCalendar(period, siblingPeriods), [period, siblingPeriods]);

    const conflicts = groupConflictNames(period.overlaps);
    const hasOverlaps = period.overlaps.length > 0;
    const hasPendingConflict = conflicts.overlapping.length > 0 || conflicts.nearby.length > 0;
    const distinctPeople = new Set(people.map(p => p.collaboratorId)).size;
    const showCalendarOnly = !hasOverlaps && calendarModel.rows.length > 1;
    const isExpandable = hasOverlaps || showCalendarOnly;

    const borderClass = hasPendingConflict
        ? PERIOD_CARD_CONFLICT_BORDER_CLASS
        : hasOverlaps
            ? PERIOD_CARD_SCHEDULED_BORDER_CLASS
            : PERIOD_CARD_DEFAULT_BORDER_CLASS;

    return (
        <div data-tour="conflicts-period-card" className={clsx('rounded-lg border border-raro-gray-200 p-4 border-l-4 shadow-sm hover:shadow-md transition-shadow', borderClass)}>
            <PeriodHeader period={period} clientName={clientName} responsibleName={responsibleName} />

            <div className="mt-4 pt-4 border-t border-raro-gray-200 flex items-center justify-between flex-wrap gap-3">
                {hasOverlaps && <div data-tour="conflicts-badges"><ConflictBadges conflicts={conflicts} /></div>}
                {!hasOverlaps && !showCalendarOnly && (
                    <span className="text-xs text-raro-gray-500">{NO_CONFLICTS_LABEL}</span>
                )}

                <div className="flex items-center gap-3 ml-auto">
                    {isExpandable && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            aria-expanded={isExpanded}
                        >
                            {isExpanded ? HIDE_DETAILS_LABEL : SHOW_DETAILS_LABEL}
                            {hasOverlaps && ` (${distinctPeople})`}
                            <ChevronDownIcon
                                className={clsx('h-3.5 w-3.5 transition-transform', isExpanded && 'rotate-180')}
                            />
                        </button>
                    )}

                    {period.isPending && onOpenRequest && (
                        <button
                            onClick={() => onOpenRequest(period.collaboratorId)}
                            className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                            data-tour="conflicts-view-request"
                        >
                            {VIEW_REQUEST_LABEL}
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && isExpandable && (
                <div className="mt-4 pt-4 border-t border-raro-gray-200 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
                    {people.length > 0 && (
                        <div data-tour="conflicts-people-panel"><ConflictPeoplePanel period={period} people={people} /></div>
                    )}
                    <div className={showCalendarOnly || people.length === 0 ? 'lg:col-span-2' : ''}>
                        <div data-tour="conflicts-calendar"><CalendarPanel period={period} people={people} model={calendarModel} /></div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClientPeriodCard;
